/**
 * Design Tokens for IntelligenceBank design system
 * Based on IntelligenceBank Brand Guidelines
 */

// ============================================================================
// IntelligenceBank Brand Colors (from style guide)
// ============================================================================

/** Primary Brand Colors */
export const brandColors = {
  // Primary Colors
  amber: '#F68D32',
  white: '#FFFFFF',
  shade: '#211C1C',

  // Secondary Colors
  goldenRays: '#FCB33F',
  skyMagenta: '#BF1767',
  midnight: '#040D4D',

  // Neutral Colors
  stone: '#727477',
  grey1: '#ADADB0',
  grey2: '#D0D4D8',
  grey3: '#F4F6F8',
} as const;

/** Amber Tonal Range */
export const amberTones = {
  darkest: '#F4770B',
  primary: '#F68D32',
  medium: '#F79E50',
  light: '#FAC596',
  lightest: '#FDECDC',
} as const;

/** Golden Rays Tonal Range */
export const goldenTones = {
  darkest: '#FB9C04',
  primary: '#FCB33F',
  medium: '#FFC05B',
  light: '#FDD493',
  lightest: '#FEF1DB',
} as const;

/** Sky Magenta Tonal Range */
export const magentaTones = {
  darkest: '#A31458',
  primary: '#BF1767',
  medium: '#E41B7B',
  light: '#EB5CA1',
  lightest: '#FBDEEC',
} as const;

/** Midnight Tonal Range (Product Only) */
export const midnightTones = {
  darkest: '#020623',
  primary: '#040D4D',
  medium: '#091DAD', // Product Only - avoid in marketing
  light: '#5266F6', // Product Only - avoid in marketing
  lightest: '#97A3FA', // Product Only - avoid in marketing
} as const;

// ============================================================================
// Dynamic Tenant Colors (from IntelligenceBank login response)
// ============================================================================

/**
 * Tenant colors returned from IntelligenceBank login response.
 * These are dynamic and set per-tenant.
 *
 * Example response:
 * {
 *   "colourPrimary": "#FFFFFF",
 *   "colourPrimaryText": "#000000",
 *   "colourSecondary": "#FCB33F",
 *   "colourSecondaryText": "#FFFFFF",
 *   "colourHighlight": "#FC3F9D",
 *   "colourHighlightText": "#FFFFFF"
 * }
 */
export interface TenantColors {
  colourPrimary: string;
  colourPrimaryText: string;
  colourSecondary: string;
  colourSecondaryText: string;
  colourHighlight: string;
  colourHighlightText: string;
}

/** Default tenant colors (IntelligenceBank brand defaults) */
export const defaultTenantColors: TenantColors = {
  colourPrimary: brandColors.white,
  colourPrimaryText: brandColors.shade,
  colourSecondary: brandColors.goldenRays,
  colourSecondaryText: brandColors.white,
  colourHighlight: brandColors.skyMagenta,
  colourHighlightText: brandColors.white,
};

// ============================================================================
// Typography (from IB Brand Guidelines)
// ============================================================================

export const typography = {
  fontFamily: {
    primary:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
  },

  // Font sizes from IB guidelines
  fontSize: {
    xs: '0.75rem', // 12px - Small
    sm: '0.875rem', // 14px - Body
    base: '1rem', // 16px - H3
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px - H2
    '2xl': '1.5rem', // 24px - H1
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  fontWeight: {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },

  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },

  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
  },
} as const;

// ============================================================================
// Spacing (from IB Brand Guidelines)
// ============================================================================

export const spacing = {
  0: '0',
  px: '1px',
  xs: '0.25rem', // 4px - XS
  sm: '0.5rem', // 8px - SM
  md: '1rem', // 16px - MD
  lg: '1.5rem', // 24px - LG
  xl: '2rem', // 32px - XL
  // Extended scale
  0.5: '0.125rem', // 2px
  1: '0.25rem', // 4px
  1.5: '0.375rem', // 6px
  2: '0.5rem', // 8px
  2.5: '0.625rem', // 10px
  3: '0.75rem', // 12px
  3.5: '0.875rem', // 14px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  7: '1.75rem', // 28px
  8: '2rem', // 32px
  9: '2.25rem', // 36px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  14: '3.5rem', // 56px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
} as const;

// ============================================================================
// Borders (from IB Brand Guidelines)
// ============================================================================

export const borders = {
  radius: {
    none: '0',
    sm: '0.25rem', // 4px - Small (buttons, inputs)
    base: '0.5rem', // 8px - Medium (cards, modals)
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px - Large (containers)
    xl: '1rem', // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
  },
  width: {
    0: '0',
    1: '1px',
    2: '2px',
    4: '4px',
  },
} as const;

// ============================================================================
// Shadows (from IB Brand Guidelines)
// ============================================================================

export const shadows = {
  none: 'none',
  sm: '0 1px 3px rgba(33, 28, 28, 0.1)', // Level 1
  base: '0 4px 6px rgba(33, 28, 28, 0.1)', // Level 2
  md: '0 4px 6px rgba(33, 28, 28, 0.1)', // Level 2
  lg: '0 10px 15px rgba(33, 28, 28, 0.1)', // Level 3
  xl: '0 20px 25px rgba(33, 28, 28, 0.1)',
  '2xl': '0 25px 50px rgba(33, 28, 28, 0.2)',
} as const;

// ============================================================================
// Animation tokens
// ============================================================================

export const animation = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// ============================================================================
// Type exports
// ============================================================================

export type BrandColors = typeof brandColors;
export type AmberTones = typeof amberTones;
export type GoldenTones = typeof goldenTones;
export type MagentaTones = typeof magentaTones;
export type MidnightTones = typeof midnightTones;
export type TypographyTokens = typeof typography;
export type Spacing = typeof spacing;
export type Borders = typeof borders;
export type Shadows = typeof shadows;
export type Animation = typeof animation;

// ============================================================================
// Re-export grouped tokens for convenience
// ============================================================================

export const colors = {
  brand: brandColors,
  amber: amberTones,
  golden: goldenTones,
  magenta: magentaTones,
  midnight: midnightTones,
} as const;

export const tokens = {
  colors,
  typography,
  spacing,
  borders,
  shadows,
  animation,
} as const;

export type Tokens = typeof tokens;